import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ActivityType, RepoHoursBreakdown } from "@torre/work-hours";
import type { ThemeClasses } from "../../../types/components";
import { commitUrl } from "../lib/github";
import { bridgeDetail, countedLines, entriesOfActivity, hoursOf, localTime, sumCost, type ActivityLine, type Ledger, type Money } from "../lib/ledger";
import { CountedLinesTable, ExpandRow, HoursAndMoney, MoneyText } from "./LedgerParts";

// ---------------------------------------------------------------------------
// «Por qué el total no coincide con la suma por repositorio y por PR» (#1342),
// en su propia tarjeta y con cada renglón desplegable (#1344):
//
//   horas que cuentan = actividad por commits + registrado sin commits
//                     + registrado con commits por encima de lo que demuestran
//                     − commits sin horas registradas (+ soporte, + otros repos)
//
// Cada renglón trae sus horas y —para los dueños— lo que valen (las de cada
// persona × su costo por hora, en MXN). Al abrirlo se ve quién hizo qué. El de
// «por encima de lo que demuestran» explica cada caso: qué se registró, qué
// prueban los commits, por qué no alcanza, y enlaza a cada commit.
// ---------------------------------------------------------------------------

interface Props {
  breakdown: RepoHoursBreakdown;
  ledger: Ledger | null;
  catalog: readonly ActivityType[] | null;
  tc: ThemeClasses;
}

type LineKey = "activity" | "withoutCommits" | "beyondEstimate" | "commitsWithoutHours" | "support" | "otherRepos";

/** Commits de una línea de actividad, cada uno con enlace a GitHub. */
function CommitLinks({ line, tc }: { line: ActivityLine; tc: ThemeClasses }) {
  const { t } = useTranslation("common");
  if (!line.commits.length) return null;
  return (
    <ul className="mt-1 space-y-0.5">
      {line.commits.map((c) => {
        const url = commitUrl({ sha: c.sha, repos: [line.repo], url: c.url });
        return (
          <li key={c.sha} className={`text-[11px] ${tc.textSecondary}`}>
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" className="font-mono text-violet-500 hover:underline">
                {c.sha.slice(0, 7)}
              </a>
            ) : (
              <span className="font-mono">{c.sha.slice(0, 7)}</span>
            )}
            {" · "}
            {localTime(c.authoredAt)}
            {c.message ? ` · ${c.message}` : ""}
          </li>
        );
      })}
      {line.commitCount > line.commits.length && <li className={`text-[11px] ${tc.textSecondary}`}>{t("workHours.ledger.moreCommits", { count: line.commitCount - line.commits.length })}</li>}
    </ul>
  );
}

/** Un caso (persona, día, repo) de la cuenta: encabezado, cifra y explicación. */
function ActivityCase({ line, minutes, money, costs, tc, children }: { line: ActivityLine; minutes: number; money: Money; costs: boolean; tc: ThemeClasses; children?: ReactNode }) {
  const { t } = useTranslation("common");
  const noRepo = t("workHours.repos.noRepo");
  return (
    <li className={`border-t py-2 ${tc.border}`} data-testid={`bridge-case-${line.userId}-${line.date}-${line.repo}`}>
      <div className={`flex justify-between gap-3 text-xs ${tc.text}`}>
        <span>
          <span className="font-semibold">{line.userName ?? line.userId}</span>
          {` · ${line.date} · ${line.repo ? (line.repo.split("/")[1] ?? line.repo) : noRepo}`}
        </span>
        <span className="whitespace-nowrap text-right">
          <span className="font-semibold">{hoursOf(minutes)}</span>
          {costs && <MoneyText money={money} className={`block text-[11px] ${tc.textSecondary}`} />}
        </span>
      </div>
      {children}
    </li>
  );
}

export default function BridgeCard({ breakdown: b, ledger, catalog, tc }: Props) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState<LineKey | null>(null);
  const detail = useMemo(() => (ledger ? bridgeDetail(ledger) : null), [ledger]);
  const counted = useMemo(() => (ledger ? countedLines(ledger) : []), [ledger]);
  const costs = Boolean(ledger?.costs);

  const money: Record<LineKey | "counted", Money> = useMemo(() => {
    if (!detail) {
      const none = sumCost([]);
      return { activity: none, withoutCommits: none, beyondEstimate: none, commitsWithoutHours: none, support: none, otherRepos: none, counted: none };
    }
    return {
      activity: sumCost(detail.activity.map((a) => a.cost)),
      withoutCommits: sumCost(detail.withoutCommits.map((e) => e.cost)),
      beyondEstimate: sumCost(detail.beyond.map((a) => a.beyondCost)),
      commitsWithoutHours: sumCost(detail.commitsWithoutHours.map((a) => a.commitsWithoutHoursCost)),
      support: sumCost(detail.support.map((s) => s.cost)),
      otherRepos: sumCost(detail.otherRepos.map((e) => e.cost)),
      counted: sumCost(counted.map((l) => l.cost)),
    };
  }, [detail, counted]);

  const lines: { key: LineKey; sign: "" | "+" | "−"; minutes: number }[] = [
    { key: "activity", sign: "", minutes: b.activityMinutes },
    { key: "withoutCommits", sign: "+", minutes: b.withoutCommitsMinutes },
    { key: "beyondEstimate", sign: "+", minutes: b.beyondEstimateMinutes },
    { key: "commitsWithoutHours", sign: "−", minutes: b.commitsWithoutHoursMinutes },
    { key: "support", sign: "+", minutes: b.supportMinutes },
    { key: "otherRepos", sign: "+", minutes: b.otherReposMinutes ?? 0 },
  ];
  const shown = lines.filter((l) => l.key === "activity" || l.minutes > 0);

  const body = (key: LineKey): ReactNode => {
    if (!detail || !ledger) return null;
    if (key === "withoutCommits") {
      return <CountedLinesTable lines={counted.filter((l) => l.key.startsWith("e:") && l.commitCount === 0 && !l.otherRepo)} costs={costs} catalog={catalog} tc={tc} testId="bridge-lines-withoutCommits" />;
    }
    if (key === "otherRepos") return <CountedLinesTable lines={counted.filter((l) => l.otherRepo)} costs={costs} catalog={catalog} tc={tc} testId="bridge-lines-otherRepos" />;
    if (key === "support") return <CountedLinesTable lines={counted.filter((l) => l.origin === "support")} costs={costs} catalog={catalog} tc={tc} testId="bridge-lines-support" />;
    if (key === "activity") {
      return (
        <ul data-testid="bridge-lines-activity">
          {detail.activity.map((a) => (
            <ActivityCase key={`${a.userId}|${a.date}|${a.repo}`} line={a} minutes={a.minutes} money={sumCost([a.cost])} costs={costs} tc={tc}>
              <p className={`text-[11px] ${tc.textSecondary}`}>
                {a.estimatedDay ? t("workHours.bridgeDetail.activityEstimated", { count: a.commitCount }) : t("workHours.bridgeDetail.activityRecorded", { count: a.commitCount, registered: hoursOf(a.registeredWithCommitsMinutes) })}
              </p>
            </ActivityCase>
          ))}
        </ul>
      );
    }
    if (key === "commitsWithoutHours") {
      return (
        <ul data-testid="bridge-lines-commitsWithoutHours">
          {detail.commitsWithoutHours.map((a) => (
            <ActivityCase key={`${a.userId}|${a.date}|${a.repo}`} line={a} minutes={a.commitsWithoutHoursMinutes} money={sumCost([a.commitsWithoutHoursCost])} costs={costs} tc={tc}>
              <p className={`text-[11px] ${tc.textSecondary}`}>
                {a.registeredWithCommitsMinutes > 0
                  ? t("workHours.bridgeDetail.lessThanCommits", { proved: hoursOf(a.minutes), registered: hoursOf(a.registeredWithCommitsMinutes) })
                  : t("workHours.bridgeDetail.noHours", { proved: hoursOf(a.minutes) })}
              </p>
              <CommitLinks line={a} tc={tc} />
            </ActivityCase>
          ))}
        </ul>
      );
    }
    // «Por encima de lo que demuestran»: qué se registró, qué prueban los commits y por qué no alcanza.
    return (
      <ul data-testid="bridge-lines-beyondEstimate">
        {detail.beyond.map((a) => {
          const entries = entriesOfActivity(ledger, a);
          const declared = entries.filter((e) => e.start && e.end).map((e) => `${e.start}–${e.end}`);
          const first = a.commits[0];
          const last = a.commits[a.commits.length - 1];
          return (
            <ActivityCase key={`${a.userId}|${a.date}|${a.repo}`} line={a} minutes={a.beyondMinutes} money={sumCost([a.beyondCost])} costs={costs} tc={tc}>
              <p className={`text-[11px] ${tc.text}`}>{t("workHours.bridgeDetail.beyondClaim", { registered: hoursOf(a.registeredWithCommitsMinutes), proved: hoursOf(a.minutes), excess: hoursOf(a.beyondMinutes) })}</p>
              <p className={`text-[11px] ${tc.textSecondary}`} data-testid="bridge-beyond-why">
                {/* Manda lo que demuestran los commits (`minutes`), no cuántos se pudieron listar. */}
                {a.minutes === 0
                  ? t("workHours.bridgeDetail.beyondNoCommits")
                  : a.commitCount === 0
                    ? t("workHours.bridgeDetail.beyondWhyNoList")
                    : t("workHours.bridgeDetail.beyondWhy", { count: a.commitCount, first: localTime(first?.authoredAt), last: localTime(last?.authoredAt) })}
              </p>
              {declared.length > 0 && <p className={`text-[11px] ${tc.textSecondary}`}>{t("workHours.bridgeDetail.declared", { ranges: declared.join(", ") })}</p>}
              {a.blocks.length > 0 && (
                <p className={`text-[11px] ${tc.textSecondary}`}>{t("workHours.bridgeDetail.dayBlocks", { blocks: a.blocks.map((x) => `${x.start}–${x.end}`).join(", ") })}</p>
              )}
              {entries.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {entries.map((e, i) => (
                    <li key={e.id ?? i} className={`text-[11px] ${tc.textSecondary}`}>
                      {t("workHours.bridgeDetail.entryLine", { hours: hoursOf(e.minutes), note: e.note ?? t("workHours.ledger.noNote"), count: e.commits.length })}
                    </li>
                  ))}
                </ul>
              )}
              <CommitLinks line={a} tc={tc} />
            </ActivityCase>
          );
        })}
      </ul>
    );
  };

  return (
    <div data-testid="repo-breakdown-bridge">
      <p className={`mb-1 text-sm font-semibold ${tc.text}`}>{t("workHours.repos.bridge.title")}</p>
      <p className={`mb-2 text-xs ${tc.textSecondary}`}>{t("workHours.repos.bridge.subtitle")}</p>
      {shown.map((l) => (
        <ExpandRow
          key={l.key}
          tc={tc}
          testId={`repo-breakdown-${l.key}`}
          open={open === l.key}
          onToggle={() => setOpen(open === l.key ? null : l.key)}
          disabled={!detail || l.minutes === 0}
          label={
            <>
              <span className={`mr-1 inline-block w-3 ${tc.textSecondary}`}>{l.sign}</span>
              {t(`workHours.repos.bridge.${l.key}`)}
              <span className={`block pl-4 text-[11px] ${tc.textSecondary}`}>{t(`workHours.repos.bridge.${l.key}Hint`)}</span>
            </>
          }
          right={<HoursAndMoney minutes={l.minutes} money={money[l.key]} costs={costs} tc={tc} />}
        >
          {body(l.key)}
        </ExpandRow>
      ))}
      <div className={`flex justify-between gap-3 border-t py-2 text-sm font-semibold ${tc.border} ${tc.text}`} data-testid="repo-breakdown-counted">
        <span>
          <span className="mr-1 inline-block w-3">=</span>
          {t("workHours.repos.bridge.counted")}
        </span>
        <span className="text-right">
          {hoursOf(b.countedMinutes)}
          {costs && <MoneyText money={money.counted} className={`block text-xs font-normal ${tc.textSecondary}`} />}
        </span>
      </div>
      {/* Sin el detalle (cargando o falló), lo registrado sin commits se sigue
          viendo con lo que ya trae el resumen. */}
      {!ledger && b.withoutCommits.length > 0 && (
        <div className="mt-2" data-testid="repo-breakdown-without-commits">
          <p className={`mb-1 text-xs font-semibold ${tc.text}`}>{t("workHours.repos.withoutCommits.title", { hours: hoursOf(b.withoutCommitsMinutes) })}</p>
          <ul className="space-y-1">
            {b.withoutCommits.map((i, n) => (
              <li key={n} className={`flex justify-between gap-3 text-xs ${tc.textSecondary}`}>
                <span>
                  <span className={tc.text}>{i.note || t("workHours.repos.withoutCommits.noNote")}</span>
                  <span className="block text-[11px]">{[i.userName, i.repo ? (i.repo.split("/")[1] ?? i.repo) : t("workHours.repos.noRepo")].filter(Boolean).join(" · ")}</span>
                </span>
                <span className={`whitespace-nowrap font-semibold ${tc.text}`}>{hoursOf(i.minutes)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {ledger?.costs && (ledger.unpricedUsers?.length ?? 0) > 0 && (
        <p className="mt-1 text-[11px] text-amber-600">{t("workHours.ledger.unpriced", { names: ledger.unpricedUsers!.map((u) => u.name).join(", ") })}</p>
      )}
      {ledger?.costs && (ledger.fxMissing?.length ?? 0) > 0 && <p className="mt-1 text-[11px] text-amber-600">{t("workHours.ledger.fxMissing", { currencies: ledger.fxMissing!.join(", ") })}</p>}
    </div>
  );
}
